import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Radio, ArrowRight, Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockLeft, setLockLeft] = useState(0); // seconds remaining while locked out
  const [brand, setBrand] = useState({ display_name: "VIS · AI SALES TEAM", logo: "" });
  useEffect(() => { api.get("/tenant/public").then((r) => { if (r.data?.display_name) setBrand(r.data); }).catch(() => {}); }, []);

  useEffect(() => {
    if (lockLeft <= 0) return;
    const t = setInterval(() => setLockLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockLeft]);

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const submit = async (e) => {
    e?.preventDefault();
    if (lockLeft > 0) return;
    setBusy(true);
    setError("");
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) { nav("/"); return; }
    if (res.retryAfter > 0) { setLockLeft(res.retryAfter); setError(""); }
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{ backgroundImage: "linear-gradient(to top, #0d0d0f 6%, rgba(13,13,15,0.82) 45%, rgba(13,13,15,0.55)), url('https://images.unsplash.com/photo-1708457753320-02e52a276a80?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')", backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="w-full max-w-md relative z-10 vis-rise">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 flex items-center justify-center" style={{ background: brand.branding?.secondary || "#1C2530" }}>
            {brand.logo ? <img src={brand.logo} alt="logo" className="w-full h-full object-contain" /> : <Radio className="w-5 h-5 text-[#F5F3EE]" strokeWidth={1.75} />}
          </div>
          <div>
            <div className="font-mono text-sm font-semibold text-[#1C2530]" data-testid="brand-display-name">{brand.display_name}</div>
            <div className="font-mono text-[9px] tracking-[0.14em] text-[#1C2530]/50 mt-0.5">
              VIRTUAL INTERACTIVE SOLUTIONZ
            </div>
          </div>
        </div>

        <div className="bg-black/55 backdrop-blur-2xl border border-white/10 p-8 rounded-lg shadow-[0_10px_50px_rgba(0,0,0,0.7)]">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/60">Sign in</div>
          <h1 className="text-3xl font-semibold tracking-tight text-white mt-1 mb-6">{brand.branding?.login_headline || "Access your workspace"}</h1>

          <button data-testid="entra-sso-btn" disabled
            className="w-full flex items-center justify-center gap-2 border border-[#DFDAD0] py-2.5 text-sm text-[#1C2530]/50 cursor-not-allowed mb-4">
            <Lock className="w-4 h-4" strokeWidth={1.5} />
            Continue with Microsoft Entra ID
            <span className="font-mono text-[9px] border border-[#DFDAD0] px-1">PROD</span>
          </button>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Email</label>
              <input data-testid="login-email" value={email} onChange={(e) => { setEmail(e.target.value); if (lockLeft > 0) setLockLeft(0); }}
                className="w-full mt-1 border border-[#DFDAD0] px-3 py-2 text-sm bg-white outline-none focus:border-[#1C2530]"
                placeholder="you@visolutionz.com" autoComplete="username" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Password</label>
              <div className="relative mt-1">
                <input data-testid="login-password" type={showPw ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[#DFDAD0] px-3 py-2 pr-10 text-sm bg-white outline-none focus:border-[#1C2530]"
                  placeholder="••••••••" autoComplete="current-password" />
                <button type="button" data-testid="login-password-toggle" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[#1C2530]/50 hover:text-[#1C2530]">
                  {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
                </button>
              </div>
            </div>
            {lockLeft > 0 && (
              <div data-testid="login-locked" className="font-mono text-xs text-[#B23A2E] border border-[#B23A2E]/40 bg-[#B23A2E]/5 px-3 py-2.5 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
                <span>Too many attempts — locked. Try again in <span data-testid="login-lock-countdown" className="font-semibold tabular-nums">{fmt(lockLeft)}</span></span>
              </div>
            )}
            {error && <div data-testid="login-error" className="font-mono text-xs text-[#B23A2E] border border-[#B23A2E]/30 bg-[#B23A2E]/5 px-3 py-2">{error}</div>}
            <button data-testid="login-submit-btn" type="submit" disabled={busy || lockLeft > 0}
              style={{ background: brand.branding?.primary || "#1C2530" }}
              className="w-full flex items-center justify-center gap-2 text-white py-2.5 text-sm font-medium transition-colors disabled:opacity-50">
              {lockLeft > 0 ? `Locked · ${fmt(lockLeft)}` : busy ? "Authenticating..." : "Sign in"} <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </form>
          <div className="mt-4 text-center">
            <a href="/admin-recovery" data-testid="admin-recovery-link" className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50 hover:text-[#1C2530]">Admin password help</a>
          </div>
        </div>
      </div>
    </div>
  );
}
